/**
 * Message queue for offline/reconnection scenarios
 * Supports in-memory queue with optional localStorage persistence
 */

import { QueuedMessage } from '../types/connectionTypes';
import { secureGet, secureSet } from './secureStorage';

const STORAGE_KEY = 'msrp_message_queue';
const MAX_QUEUE_SIZE = 100;
const OVERFLOW_THRESHOLD = 0.8; // 80% full

export class MessageQueue {
  private queue: QueuedMessage[] = [];
  private maxSize: number;
  private persistToStorage: boolean = false;

  constructor(maxSize: number = MAX_QUEUE_SIZE) {
    this.maxSize = maxSize;
    this.restoreFromLocalStorage();
  }

  /**
   * Add message to queue
   * Drops oldest message if queue is full (FIFO overflow)
   */
  enqueue(message: QueuedMessage): void {
    // Check overflow
    if (this.queue.length >= this.maxSize) {
      const dropped = this.queue.shift();
      console.warn(`Message queue overflow: dropped message ${dropped?.id}`);
    }

    // Warn when approaching capacity
    if (this.queue.length >= this.maxSize * OVERFLOW_THRESHOLD) {
      console.warn(`Message queue is ${Math.round(this.queue.length / this.maxSize * 100)}% full`);
    }

    this.queue.push(message);

    // Persist if enabled
    if (this.persistToStorage) {
      this.saveToLocalStorage();
    }
  }

  /**
   * Remove and return first message from queue
   * Returns null if queue is empty
   */
  dequeue(): QueuedMessage | null {
    const message = this.queue.shift();

    if (message && this.persistToStorage) {
      this.saveToLocalStorage();
    }

    return message || null;
  }

  /**
   * Peek at first message without removing it
   */
  peek(): QueuedMessage | null {
    return this.queue[0] || null;
  }

  /**
   * Get all queued messages (without removing)
   */
  getAll(): QueuedMessage[] {
    return [...this.queue];
  }

  /**
   * Clear all messages from queue
   */
  clear(): void {
    this.queue = [];
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Get current queue size
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Check if queue is empty
   */
  isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Check if queue is full
   */
  isFull(): boolean {
    return this.queue.length >= this.maxSize;
  }

  /**
   * Get queue capacity usage percentage
   */
  getCapacityPercentage(): number {
    return Math.round((this.queue.length / this.maxSize) * 100);
  }

  /**
   * Enable localStorage persistence
   */
  enablePersistence(): void {
    this.persistToStorage = true;
    this.saveToLocalStorage();
  }

  /**
   * Disable localStorage persistence
   */
  disablePersistence(): void {
    this.persistToStorage = false;
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Save queue to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      secureSet(localStorage, STORAGE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save message queue to localStorage:', error);
      // If quota exceeded, clear old data
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing queue storage');
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  /**
   * Restore queue from localStorage
   */
  private restoreFromLocalStorage(): void {
    try {
      const stored = secureGet(localStorage, STORAGE_KEY);
      if (stored) {
        this.queue = JSON.parse(stored);
        console.log(`Restored ${this.queue.length} messages from localStorage`);
      }
    } catch (error) {
      console.error('Failed to restore message queue from localStorage:', error);
      this.queue = [];
    }
  }

  /**
   * Remove stale messages older than maxAge (in milliseconds)
   */
  removeStaleMessages(maxAge: number = 5 * 60 * 1000): number {
    const now = Date.now();
    const initialSize = this.queue.length;

    this.queue = this.queue.filter(message => {
      const age = now - message.timestamp;
      if (age > maxAge) {
        console.warn(`Removing stale message ${message.id} (age: ${Math.round(age / 1000)}s)`);
        return false;
      }
      return true;
    });

    const removedCount = initialSize - this.queue.length;

    if (removedCount > 0 && this.persistToStorage) {
      this.saveToLocalStorage();
    }

    return removedCount;
  }

  /**
   * Get queue statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    capacityPercentage: number;
    oldestMessageAge: number | null;
    newestMessageAge: number | null;
  } {
    const now = Date.now();
    const oldest = this.queue[0];
    const newest = this.queue[this.queue.length - 1];

    return {
      size: this.queue.length,
      maxSize: this.maxSize,
      capacityPercentage: this.getCapacityPercentage(),
      oldestMessageAge: oldest ? now - oldest.timestamp : null,
      newestMessageAge: newest ? now - newest.timestamp : null,
    };
  }
}
